import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import countryCodes from "../data/countryCodes"; // Import des codes pays

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const UserEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    email: "",
    phone: "", // Seulement les chiffres (sans indicatif)
    profile: "",
    statut_user: 1,
    matricule_user: "",
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [originalProfile, setOriginalProfile] = useState("");

  // États pour le sélecteur d'indicatif
  const [showCountryList, setShowCountryList] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countryCodes?.find(c => c.code === "+227") || countryCodes?.[0] || { 
      code: "+227", 
      name: "Niger", 
      flag: "🇳🇪",
      phoneLength: 8,
      pattern: "^\\+227\\d{8}$"
    }
  );
  const [searchCountry, setSearchCountry] = useState("");
  const countryListRef = useRef(null);

  // Fermer le dropdown en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryListRef.current && !countryListRef.current.contains(event.target)) {
        setShowCountryList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Charger l'utilisateur connecté
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUserProfile(String(user.profile));
        setCurrentUserId(Number(user.id));
        setIsOwnProfile(Number(user.id) === Number(id));
      } catch (e) {
        console.error("Erreur parse user:", e);
      }
    }
  }, [id]);

  // Fonction pour extraire l'indicatif d'un numéro
  const extractCountryFromPhone = useCallback((phoneNumber) => {
    if (!phoneNumber) return null;
    
    // Chercher le pays correspondant à l'indicatif
    const matchedCountry = countryCodes?.find(country => 
      country && phoneNumber.startsWith(country.code)
    );
    
    return matchedCountry || selectedCountry;
  }, [selectedCountry]);

  // Fonction pour extraire les chiffres seulement
  const extractPhoneDigits = useCallback((phoneNumber) => {
    if (!phoneNumber) return "";
    
    // Chercher l'indicatif
    const country = extractCountryFromPhone(phoneNumber);
    if (country) {
      return phoneNumber.replace(country.code, "");
    }
    
    return phoneNumber.replace(/\D/g, "");
  }, [extractCountryFromPhone]);

  // Charger l'utilisateur à modifier
  const loadUser = useCallback(async () => {
    setLoading(true);
    setServerError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users/get.php?id=${id}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.data?.success && response.data.user) {
        const user = response.data.user;
        setUserData(user);
        setOriginalProfile(String(user.profile || ""));
        
        // Extraire l'indicatif et les chiffres du numéro
        const userCountry = extractCountryFromPhone(user.phone || "");
        const userPhoneDigits = extractPhoneDigits(user.phone || "");
        
        if (userCountry) {
          setSelectedCountry(userCountry);
        }
        
        // Remplir le formulaire
        setForm({
          nom: user.nom || "",
          prenom: user.prenom || "",
          email: user.email || "",
          phone: userPhoneDigits, // Stocker seulement les chiffres
          profile: String(user.profile || ""),
          statut_user: Number(user.statut_user || 1),
          matricule_user: user.matricule || "",
        });

      } else {
        setServerError(response.data?.message || "Utilisateur introuvable.");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 404) {
        setServerError("Utilisateur introuvable ou supprimé.");
      } else if (err.response?.status === 403) {
        setServerError("Vous n'avez pas la permission de modifier cet utilisateur.");
      } else {
        setServerError("Impossible de charger les données.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate, extractCountryFromPhone, extractPhoneDigits]);

  useEffect(() => {
    if (id) {
      loadUser();
    }
  }, [id, loadUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // NE PAS autoriser la modification du profil
    if (name === "profile") {
      return;
    }
    
    setForm(prev => ({
      ...prev,
      [name]: name === "statut_user" ? Number(value) : value,
    }));
    
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Fonction pour générer exemple
  const generatePhoneExample = (phoneLength) => {
    if (!phoneLength) return "";
    let example = "";
    for (let i = 0; i < phoneLength; i++) {
      example += (i % 10).toString();
    }
    return example;
  };

  const validate = () => {
    const e = {};
    
    // Lettres (accentuées), espaces, tirets et apostrophes uniquement — pas de
    // chiffres ni de caractères spéciaux dans un nom/prénom.
    const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
    if (!form.nom.trim()) {
      e.nom = "Le nom est requis.";
    } else if (!nameRegex.test(form.nom.trim())) {
      e.nom = "Le nom ne doit contenir que des lettres.";
    }
    if (!form.prenom.trim()) {
      e.prenom = "Le prénom est requis.";
    } else if (!nameRegex.test(form.prenom.trim())) {
      e.prenom = "Le prénom ne doit contenir que des lettres.";
    }
    
    if (!form.email.trim()) {
      e.email = "L'email est requis.";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      e.email = "Format d'email invalide.";
    }
    
    // Validation téléphone - DYNAMIQUE PAR PAYS
    const phoneDigits = form.phone.replace(/\D/g, "");
    const expectedLength = selectedCountry?.phoneLength;
    
    if (!phoneDigits) {
      e.phone = "Le téléphone est requis.";
    } else if (!/^\d+$/.test(phoneDigits)) {
      e.phone = "Le numéro doit contenir uniquement des chiffres";
    } else if (expectedLength && phoneDigits.length !== expectedLength) {
      e.phone = `Numéro ${selectedCountry?.name || ""} invalide (${phoneDigits.length} chiffres, besoin de ${expectedLength} chiffres)`;
    } else if (!expectedLength && (phoneDigits.length < 8 || phoneDigits.length > 15)) {
      e.phone = `Numéro invalide (${phoneDigits.length} chiffres, doit être entre 8 et 15 chiffres)`;
    }
    
    // Validation matricule selon profil
    if (form.profile !== "1" && !form.matricule_user.trim()) {
      e.matricule_user = "Le matricule est obligatoire pour ce profil.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    
    if (!validate()) return;
    
    setSaving(true);

    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/users/update.php`;

      // Formater le numéro avec l'indicatif
      const phoneDigits = form.phone.replace(/\D/g, "");
      const phoneFullNumber = selectedCountry?.code + phoneDigits;

      // Données à envoyer
      const requestData = {
        id: parseInt(id),
        nom: form.nom,
        prenom: form.prenom,
        email: form.email,
        phone: phoneFullNumber, // Utiliser le numéro formaté avec indicatif
        statut_user: Number(form.statut_user),
        matricule_user: form.matricule_user || "",
      };

      console.log("Numéro téléphone envoyé:", phoneFullNumber);

      const response = await axios.post(
        url,
        requestData,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        const message = `✅ Utilisateur modifié avec succès.\n\n` +
                       `👤 ${response.data.user.prenom} ${response.data.user.nom}\n` +
                       `📧 ${response.data.user.email}\n` +
                       `👨‍💼 Profil: ${getProfileName(response.data.user.profile)}\n` +
                       `📞 ${response.data.user.phone || "Non renseigné"}\n` +
                       (response.data.user.matricule ? `🔢 Matricule: ${response.data.user.matricule}\n` : "") +
                       `📊 Statut: ${response.data.user.statut_user === 1 ? 'Actif' : 'Inactif'}`;

        alert(message);

        // Mettre à jour localStorage si c'est l'utilisateur connecté
        if (isOwnProfile) {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            try {
              const current = JSON.parse(userStr);
              const updatedUser = {
                ...current,
                nom: form.nom,
                prenom: form.prenom,
                email: form.email,
                phone: phoneFullNumber,
              };
              localStorage.setItem("user", JSON.stringify(updatedUser));
            } catch (e) {
              console.error("Erreur mise à jour localStorage:", e);
            }
          }
        }

        // Recharger les données
        loadUser();
        
      } else {
        setServerError(
          response.data?.message ||
          "Impossible de modifier l'utilisateur."
        );
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 409) {
        if (err.response.data?.message?.includes("matricule")) {
          setServerError("Ce matricule est déjà utilisé.");
        } else if (err.response.data?.message?.includes("profil")) {
          setServerError(err.response.data.message);
          setForm(prev => ({ ...prev, profile: originalProfile }));
        } else {
          setServerError("Cet email est déjà utilisé.");
        }
      } else if (err.response?.status === 403) {
        // Vérifier si c'est une erreur d'abonnement
        if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setServerError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setServerError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else {
          setServerError("Permission refusée. " + (err.response.data?.message || ""));
        }
      } else if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 400 && err.response.data?.errors) {
        const validationErrors = {};
        err.response.data.errors.forEach(error => {
          if (error.includes("nom")) validationErrors.nom = error;
          else if (error.includes("prénom")) validationErrors.prenom = error;
          else if (error.includes("email")) validationErrors.email = error;
          else if (error.includes("matricule")) validationErrors.matricule_user = error;
          else if (error.includes("téléphone")) validationErrors.phone = error;
        });
        setErrors(validationErrors);
        setServerError("Veuillez corriger les erreurs ci-dessous.");
      } else {
        setServerError("Erreur technique. Veuillez réessayer.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    
    // Déterminer la longueur max selon le pays
    const maxLength = selectedCountry?.phoneLength || 15;
    
    // Garder seulement les chiffres
    const digitsOnly = value.replace(/\D/g, "");
    // Limiter selon la longueur du pays
    const limitedDigits = digitsOnly.substring(0, maxLength);
    
    setForm(prev => ({ 
      ...prev, 
      phone: limitedDigits 
    }));
    
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: "" }));
    }
  };

  // Fonction pour obtenir le nom du profil
  const getProfileName = (profileCode) => {
    const profiles = {
      "1": "Super Admin",
      "2": "Contrôleur",
      "3": "Gestionnaire",
      "4": "Agent",
      "5": "Poste de contrôle"
    };
    return profiles[profileCode] || `Profil ${profileCode}`;
  };

  // Rendre le champ en lecture seule si c'est le propre profil
  const isFieldDisabled = (fieldName) => {
    if (isOwnProfile) {
      return fieldName === "statut_user" || fieldName === "profile";
    }
    if (fieldName === "profile") {
      return true;
    }
    return saving;
  };

  /* ====== COMPOSANT COUNTRYSELECTOR ====== */
  const CountrySelector = ({ 
    showList, setShowList, selectedCountry, setSelectedCountry, 
    search, setSearch, listRef, disabled 
  }) => {
    // Filtrer les pays de manière SÉCURISÉE
    const filteredCountries = (countryCodes || []).filter(country => {
      if (!country) return false;
      
      const searchTerm = (search || "").toLowerCase();
      const countryName = String(country.name || country.label || "").toLowerCase();
      const countryCode = String(country.code || "").toLowerCase();
      
      return countryName.includes(searchTerm) || countryCode.includes(searchTerm);
    });

    return (
      <div ref={listRef} className="position-relative">
        <button
          type="button"
          className="btn d-flex align-items-center justify-content-between"
          onClick={() => setShowList(!showList)}
          disabled={disabled}
          style={{
            backgroundColor: disabled ? '#F0F0F0' : '#F5F7FA',
            border: `1px solid ${errors.phone ? '#f44336' : '#e0e0e0'}`,
            borderRadius: '8px',
            padding: '8px 12px',
            width: '120px',
            minHeight: '44px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
        >
          <span className="me-2" style={{ fontSize: '1.2rem' }}>{selectedCountry?.flag || "🏳️"}</span>
          <span style={{ fontSize: '0.9rem', color: '#2E2E2E', fontWeight: '500' }}>
            {selectedCountry?.code || "+227"}
          </span>
          <i className="bi bi-chevron-down ms-2" style={{ 
            fontSize: '0.8rem', 
            color: disabled ? '#999' : '#666' 
          }}></i>
        </button>

        {showList && !disabled && (
          <div style={{
            position: 'absolute', top: '100%', left: '0', width: '280px', maxHeight: '300px',
            backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, overflow: 'hidden', marginTop: '4px'
          }}>
            <div className="p-2 border-bottom">
              <div className="input-group">
                <span className="input-group-text" style={{ 
                  backgroundColor: '#F5F7FA', borderColor: '#e0e0e0', padding: '8px 12px'
                }}>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un pays..."
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ fontSize: '0.9rem', borderColor: '#e0e0e0', padding: '8px 12px' }}
                  autoFocus
                />
              </div>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredCountries.map((country, index) => {
                if (!country) return null;
                
                return (
                  <button
                    key={index}
                    type="button"
                    className="btn btn-light text-start w-100 border-0 rounded-0"
                    onClick={() => {
                      if (country) {
                        setSelectedCountry(country);
                        setShowList(false);
                        setSearch("");
                      }
                    }}
                    style={{ 
                      padding: '10px 16px', 
                      borderBottom: '1px solid #f0f0f0', 
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <div className="d-flex align-items-center">
                      <span className="me-3" style={{ fontSize: '1.2rem', minWidth: '30px' }}>
                        {country?.flag || "🏳️"}
                      </span>
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: '500' }}>
                          {country?.name || country?.label || "Pays"}
                        </div>
                        <small style={{ color: '#666' }}>{country?.code || ""}</small>
                        {country?.phoneLength && (
                          <small style={{ color: '#666', display: 'block' }}>
                            {country.phoneLength} chiffres
                          </small>
                        )}
                      </div>
                      {selectedCountry?.code === country?.code && <i className="bi bi-check text-primary"></i>}
                    </div>
                  </button>
                );
              })}
              
              {filteredCountries.length === 0 && (
                <div className="text-center py-3 text-muted" style={{ fontSize: '0.9rem' }}>
                  Aucun pays trouvé
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>
          {isOwnProfile ? "Mon profil" : "Éditer l'utilisateur"}
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/dashboard")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Dashboard
              </button>
            </li>
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/users")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Utilisateurs
              </button>
            </li>
            <li className="breadcrumb-item active" style={{ color: '#0B3C5D' }}>
              {isOwnProfile ? "Mon profil" : "Modifier"}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: '#0B3C5D',
                  borderBottom: '2px solid #F5F7FA',
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-person-gear me-2" style={{ color: '#1E88C8' }}></i>
                  {isOwnProfile ? "Modifier mon profil" : "Modifier l'utilisateur"}
                </h5>

                {/* Avertissement si utilisateur inactif */}
                {userData?.statut_user === 0 && !isOwnProfile && (
                  <div className="alert alert-warning mb-4" style={{ 
                    backgroundColor: '#fff8e1',
                    borderColor: '#ffc107',
                    color: '#ff8f00',
                    borderRadius: '8px'
                  }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Cet utilisateur est actuellement inactif.</strong>
                    <br/>
                    Vous pouvez le réactiver en changeant son statut à "Actif".
                  </div>
                )}

                {/* Avertissement si modification de son propre profil */}
                {isOwnProfile && (
                  <div className="alert alert-info mb-4" style={{ 
                    backgroundColor: '#E3F2FD',
                    borderColor: '#4FC3F7',
                    color: '#0B3C5D',
                    borderRadius: '8px'
                  }}>
                    <i className="bi bi-person-circle me-2"></i>
                    <strong>Vous modifiez votre propre profil.</strong>
                    <br/>
                    Vous ne pouvez pas modifier votre statut.
                  </div>
                )}

                {serverError && (
                  <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: '#f44336',
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: '4px solid #f44336',
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{serverError}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setServerError("")}
                      ></button>
                    </div>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: '#0B3C5D' }}>
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-3" style={{ color: '#2E2E2E' }}>Chargement des informations...</p>
                  </div>
                ) : (
                  <form className="row g-3" onSubmit={handleSubmit}>
                    {/* Informations utilisateur */}
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#E3F2FD',
                          color: '#0B3C5D'
                        }}>
                          <i className="bi bi-person-badge"></i>
                        </div>
                        <h6 style={{ color: '#0B3C5D', fontWeight: '600', marginBottom: '0' }}>
                          Informations utilisateur
                        </h6>
                      </div>
                    </div>

                    {/* Ligne 1 - Prénom, Nom */}
                    <div className="col-md-6">
                      <label htmlFor="prenom" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-person me-1" style={{ color: '#1E88C8' }}></i>
                        Prénom <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#F5F7FA',
                          borderColor: errors.prenom ? '#f44336' : '#e0e0e0',
                          color: errors.prenom ? '#f44336' : '#1E88C8'
                        }}>
                          <i className="bi bi-person-vcard"></i>
                        </span>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          className={`form-control ${errors.prenom ? "is-invalid" : ""}`}
                          value={form.prenom}
                          onChange={handleChange}
                          disabled={saving}
                          placeholder="Ex: Jean"
                          autoComplete="given-name"
                          style={{ 
                            borderColor: errors.prenom ? '#f44336' : '#e0e0e0',
                            borderRadius: '0 8px 8px 0',
                            color: '#2E2E2E'
                          }}
                        />
                      </div>
                      {errors.prenom && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.prenom}</div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="nom" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-person me-1" style={{ color: '#1E88C8' }}></i>
                        Nom <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#F5F7FA',
                          borderColor: errors.nom ? '#f44336' : '#e0e0e0',
                          color: errors.nom ? '#f44336' : '#1E88C8'
                        }}>
                          <i className="bi bi-person-vcard"></i>
                        </span>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          className={`form-control ${errors.nom ? "is-invalid" : ""}`}
                          value={form.nom}
                          onChange={handleChange}
                          disabled={saving}
                          placeholder="Ex: Dupont"
                          autoComplete="family-name"
                          style={{ 
                            borderColor: errors.nom ? '#f44336' : '#e0e0e0',
                            borderRadius: '0 8px 8px 0',
                            color: '#2E2E2E'
                          }}
                        />
                      </div>
                      {errors.nom && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.nom}</div>
                      )}
                    </div>

                    {/* Ligne 2 - Email, Téléphone avec indicateur */}
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-envelope me-1" style={{ color: '#1E88C8' }}></i>
                        Email <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#F5F7FA',
                          borderColor: errors.email ? '#f44336' : '#e0e0e0',
                          color: errors.email ? '#f44336' : '#1E88C8'
                        }}>
                          <i className="bi bi-at"></i>
                        </span>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`form-control ${errors.email ? "is-invalid" : ""}`}
                          value={form.email}
                          onChange={handleChange}
                          disabled={saving}
                          placeholder="exemple@email.com"
                          autoComplete="email"
                          style={{ 
                            borderColor: errors.email ? '#f44336' : '#e0e0e0',
                            borderRadius: '0 8px 8px 0',
                            color: '#2E2E2E'
                          }}
                        />
                      </div>
                      {errors.email && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.email}</div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-phone me-1" style={{ color: '#1E88C8' }}></i>
                        Téléphone <span className="text-danger">*</span>
                      </label>
                      
                      <div className="d-flex align-items-stretch" style={{ gap: '8px' }}>
                        <CountrySelector
                          showList={showCountryList}
                          setShowList={setShowCountryList}
                          selectedCountry={selectedCountry}
                          setSelectedCountry={setSelectedCountry}
                          search={searchCountry}
                          setSearch={setSearchCountry}
                          listRef={countryListRef}
                          disabled={saving}
                        />
                        
                        <div className="position-relative" style={{ flex: '1' }}>
                          <div style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            fontSize: '0.95rem',
                            pointerEvents: 'none',
                            zIndex: 5
                          }}>
                            {selectedCountry?.code || "+227"}
                          </div>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            value={form.phone}
                            onChange={handlePhoneChange}
                            placeholder={selectedCountry?.phoneLength ? 
                              `Ex: ${generatePhoneExample(selectedCountry.phoneLength)}` : 
                              "Numéro de téléphone"}
                            disabled={saving}
                            maxLength={selectedCountry?.phoneLength || 15}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            style={{ 
                              borderRadius: '8px',
                              borderColor: errors.phone ? '#f44336' : '#e0e0e0',
                              padding: '12px 16px 12px 55px',
                              fontSize: '0.95rem',
                              width: '100%',
                              color: '#2E2E2E'
                            }}
                          />
                        </div>
                      </div>
                      
                      {errors.phone && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>
                          {errors.phone}
                        </div>
                      )}
                      
                      <small className="text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                        Format: {selectedCountry?.code || "+227"} + {selectedCountry?.phoneLength || "?"} chiffres
                        {selectedCountry?.phoneLength && 
                          ` (ex: ${selectedCountry.code}${generatePhoneExample(selectedCountry.phoneLength)})`}
                      </small>
                    </div>

                    {/* Ligne 3 - Profil (lecture seule), Matricule */}
                    <div className="col-md-6">
                      <label htmlFor="profile" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-person-badge me-1" style={{ color: '#1E88C8' }}></i>
                        Profil
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          id="profile"
                          name="profile"
                          className="form-control bg-light"
                          value={getProfileName(form.profile)}
                          readOnly
                          disabled
                          style={{ 
                            cursor: "not-allowed",
                            backgroundColor: '#F5F7FA',
                            borderColor: '#e0e0e0',
                            color: '#2E2E2E'
                          }}
                        />
                        <span className="input-group-text" style={{ backgroundColor: '#F5F7FA', color: '#757575' }}>
                          <i className="bi bi-lock"></i>
                        </span>
                      </div>
                      <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Le profil est définitif et ne peut pas être modifié
                      </small>
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="matricule_user" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-card-text me-1" style={{ color: '#1E88C8' }}></i>
                        Matricule {form.profile !== "1" && <span className="text-danger">*</span>}
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#F5F7FA',
                          borderColor: errors.matricule_user ? '#f44336' : '#e0e0e0',
                          color: errors.matricule_user ? '#f44336' : '#1E88C8'
                        }}>
                          <i className="bi bi-123"></i>
                        </span>
                        <input
                          type="text"
                          id="matricule_user"
                          name="matricule_user"
                          className={`form-control ${errors.matricule_user ? "is-invalid" : ""}`}
                          value={form.matricule_user}
                          onChange={handleChange}
                          disabled={saving || form.profile === "1"}
                          placeholder={form.profile === "1" ? "Optionnel" : "Ex: CTRL1234"}
                          autoComplete="off"
                          style={{ 
                            borderColor: errors.matricule_user ? '#f44336' : '#e0e0e0',
                            borderRadius: '0 8px 8px 0',
                            color: '#2E2E2E'
                          }}
                        />
                      </div>
                      {errors.matricule_user && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.matricule_user}</div>
                      )}
                      <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                        {form.profile === "1"
                          ? "Optionnel pour Super Admin" 
                          : "Obligatoire pour ce profil"}
                      </small>
                    </div>

                    {/* Ligne 4 - Statut, Informations supplémentaires */}
                    <div className="col-md-6">
                      <label htmlFor="statut_user" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-toggle-on me-1" style={{ color: '#1E88C8' }}></i>
                        Statut {!isOwnProfile && <span className="text-danger">*</span>}
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#F5F7FA',
                          borderColor: '#e0e0e0',
                          color: '#1E88C8'
                        }}>
                          <i className="bi bi-person-check"></i>
                        </span>
                        <select
                          id="statut_user"
                          name="statut_user"
                          className="form-select"
                          value={form.statut_user}
                          onChange={handleChange}
                          disabled={isFieldDisabled("statut_user") || saving}
                          style={{ 
                            borderColor: '#e0e0e0',
                            color: '#2E2E2E'
                          }}
                        >
                          <option value={1}>Actif</option>
                          <option value={0}>Inactif</option>
                        </select>
                      </div>
                      <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                        {form.statut_user === 0 
                          ? "L'utilisateur ne pourra pas se connecter" 
                          : "L'utilisateur pourra se connecter normalement"}
                      </small>
                    </div>

                    {/* Informations supplémentaires */}
                    {userData && (
                      <div className="col-md-6">
                        <div className="card" style={{ 
                          backgroundColor: '#F5F7FA',
                          border: 'none',
                          borderRadius: '8px'
                        }}>
                          <div className="card-body py-3">
                            <h6 style={{ color: '#0B3C5D', fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px' }}>
                              <i className="bi bi-info-circle me-1"></i>
                              Informations système
                            </h6>
                            <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>
                              <i className="bi bi-calendar me-1" style={{ color: '#1E88C8' }}></i>
                              Créé le: {new Date(userData.created_at).toLocaleDateString('fr-FR')}
                            </small>
                            {userData.updated_at && (
                              <small className="text-muted d-block mt-2" style={{ fontSize: '0.85rem' }}>
                                <i className="bi bi-clock-history me-1" style={{ color: '#1E88C8' }}></i>
                                Modifié le: {new Date(userData.updated_at).toLocaleDateString('fr-FR')}
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Boutons */}
                    <div className="col-12 d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: '#F5F7FA' }}>
                      <button 
                        type="submit" 
                        className="btn fw-semibold"
                        disabled={saving}
                        style={{
                          color: 'white',
                          backgroundColor: saving ? '#B0BEC5' : '#0B3C5D',
                          borderColor: saving ? '#B0BEC5' : '#0B3C5D',
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          opacity: saving ? 0.8 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) {
                            e.target.style.backgroundColor = '#1E88C8';
                            e.target.style.borderColor = '#1E88C8';
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!saving) {
                            e.target.style.backgroundColor = '#0B3C5D';
                            e.target.style.borderColor = '#0B3C5D';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Enregistrement...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Enregistrer les modifications
                          </>
                        )}
                      </button>
                      
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={() => navigate("/users")}
                        disabled={saving}
                        style={{
                          color: '#2E2E2E',
                          borderColor: '#e0e0e0',
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '12px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#F5F7FA';
                          e.target.style.borderColor = '#0B3C5D';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = '#e0e0e0';
                        }}
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        Retour à la liste
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: #1E88C8;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: #F5F7FA;
          cursor: not-allowed;
        }
        
        .form-control.is-invalid {
          border-color: #f44336;
        }
        
        .invalid-feedback {
          color: #f44336;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default UserEdit;